'use strict'

import {UNIT_KINDS} from '../constants'
import {isEligibleSourceLotStatus} from './sourceLotEligibility'

const PIN_KINDS = new Set([UNIT_KINDS.HOUSE_PIN, UNIT_KINDS.CUSTOMER_PIN])
const RECONCILABLE_KINDS = new Set([UNIT_KINDS.BAR, ...PIN_KINDS])

export const isPinKind = (kind) => PIN_KINDS.has(kind)

export const decorateSourceLotsWithReconciliation = (sourceLots, registry) =>
  (Array.isArray(sourceLots) ? sourceLots : []).map((lot) => ({
    ...lot,
    units: (Array.isArray(lot?.units) ? lot.units : []).map((unit) => ({
      ...unit,
      reconciliation: registry?.[unit?.id] ? {...registry[unit.id]} : null,
    })),
  }))

export const isSourceUnitReconciled = (unit) => Boolean(unit?.reconciliation?.reconciledAt)

export const reconciliationBuckets = (sourceLots) => {
  const buckets = {free: [], allocated: [], reconciled: []}
  for (const lot of sourceLots || []) {
    if (!isEligibleSourceLotStatus(lot?.status)) continue
    for (const unit of lot?.units || []) {
      if (!RECONCILABLE_KINDS.has(unit?.kind)) continue
      const row = {
        ...unit,
        sourceLotId: unit?.sourceLotId || lot?.id || null,
        sourceLotCode: unit?.sourceLotCode || lot?.lotCode || '',
      }
      if (row.allocatedMasterLotId) buckets.allocated.push(row)
      else if (isSourceUnitReconciled(row)) buckets.reconciled.push(row)
      else buckets.free.push(row)
    }
  }
  return {
    free: sortLikelyOnHandUnits(buckets.free),
    allocated: stableIdentitySort(buckets.allocated),
    reconciled: stableIdentitySort(buckets.reconciled),
  }
}

export const reconciliationIdsForComplement = (sourceLots, keeperIds) => {
  const keepers = new Set((keeperIds || []).map(String))
  return reconciliationBuckets(sourceLots).free
    .filter(({id}) => !keepers.has(String(id)))
    .map(({id}) => id)
}

export const upsertSourceUnitReconciliations = (
  registry,
  unitIds,
  options = {},
) => {
  const {reconciledAt, note} = options
  const next = {...(registry || {})}
  const timestamp = reconciledAt || new Date().toISOString()
  const hasNote = Object.prototype.hasOwnProperty.call(options, 'note')
  for (const id of uniqueUnitIds(unitIds)) {
    const record = {...(next[id] || {}), reconciledAt: timestamp}
    if (hasNote) {
      const normalizedNote = typeof note === 'string' ? note.trim() : ''
      if (normalizedNote) record.note = normalizedNote
      else delete record.note
    }
    next[id] = record
  }
  return next
}

export const recoverSourceUnitReconciliations = (registry, unitIds) => {
  const next = {...(registry || {})}
  for (const id of uniqueUnitIds(unitIds)) delete next[id]
  return next
}

export const sortLikelyOnHandUnits = (units) =>
  [...(Array.isArray(units) ? units : [])].sort(compareLikelyOnHand)

const compareLikelyOnHand = (left, right) => {
  const leftTime = assayTime(left)
  const rightTime = assayTime(right)
  if (leftTime !== rightTime) return rightTime - leftTime
  const kindDelta = kindRank(left?.kind) - kindRank(right?.kind)
  if (kindDelta !== 0) return kindDelta
  return stableIdentity(left).localeCompare(stableIdentity(right))
}

const assayTime = (unit) => {
  const time = new Date(unit?.assay?.completedAt || '').getTime()
  return Number.isFinite(time) ? time : -Infinity
}

const kindRank = (kind) => {
  if (isPinKind(kind)) return 0
  if (kind === UNIT_KINDS.BAR) return 1
  return 2
}

const stableIdentity = (unit) => `${unit?.sourceLotCode || ''}\u0000${unit?.id || ''}`

const stableIdentitySort = (units) => [...units].sort((left, right) =>
  stableIdentity(left).localeCompare(stableIdentity(right))
)

const uniqueUnitIds = (unitIds) => [...new Set(
  (Array.isArray(unitIds) ? unitIds : []).filter(Boolean).map(String)
)]
